/**
 * サーバーの鍵（sk_）の一覧・「まとめて作り直す」と、パスワード再設定の結果（外したログイン方法・残っている sk_）の判断（純関数）。
 * 2026-09-28 creator/dashboard-pass（Core SEC-PKLOGIN-01 の続き・Kumo 管理の判断 (b)）。
 * 画面は pages/ResetDone.tsx（再設定の完了）・pages/AccountSecretKeys.tsx（/account/secret-keys と /rotate）。
 * テストは secretKeys.test.ts（.tsx は node で読めないので、判断はここに集める）。
 *
 * サーバーの正（Core・読むだけ）: apps/server/src/modules/platform/secretKeys.ts・recovery.ts・ownership.ts と同じフォルダの README。
 *   POST /v1/developers/password/reset { token, password } → { ok, removedSignInMethods: [...], secretKeys: [SecretKeyView] }
 *   GET  /v1/developers/me/secret-keys/rotate-all/preview → { keys: [SecretKeyView], version }
 *   POST /v1/developers/me/secret-keys/rotate-all { version, password? | reauthToken? }
 *        → { revoked: [SecretKeyView], created: [{ id, projectId, projectName, environment, label, token, replaces }], functionsRedeployRequired: [...] }
 *        409 keys_changed（details.version）/ 409 no_secret_keys / 403 email_unverified / 400・401 再認証
 *
 * ■ 秘密の扱い（指示書・Core との合意）
 *   - 新しい鍵の値（created[].token）は rotate-all の応答の 1 回だけ。画面のコンポーネントの state（メモリ）にだけ置き、
 *     localStorage・sessionStorage・URL・history.state には置かない（離れたら二度と見られない = サーバーは sha256 しか持たない）。
 *   - 再設定の結果（鍵の値は無い・displayPrefix だけ）も、このモジュールの変数（メモリ）にだけ置く。
 *     WHY history.state（navigate の state）に載せないか: ブラウザがセッション履歴としてディスクに残しうる。
 *     「どのプロジェクトにどの鍵があるか」は秘密ではないが、他人に見せたい情報でもない = 残さない方を選ぶ（再読み込みで消える = ログインへ）。
 *   - 利用者が付けた名前（パスキーの label・鍵の label・プロジェクト名・外部 ID のメール）は、サーバーで掃除済み（cleanUserLabel）だが、
 *     画面では必ずテキストとして出す（React の {} = 文字として描く。HTML として解釈させる書き方をしない）。
 */

/** 再設定の完了の画面（pages/ResetDone.tsx・App.tsx のルート） */
export const RESET_DONE_PATH = '/reset-password/done';
/** サーバーの鍵の一覧・まとめて作り直す（ログイン必須。再設定の完了からはログイン画面の state.from で戻ってくる。通知メールのボタンもここ） */
export const SECRET_KEYS_PATH = '/account/secret-keys';
export const ROTATE_PATH = '/account/secret-keys/rotate';

export type EnvName = 'development' | 'production';
export type KeyCreatedVia = 'dashboard' | 'cli' | 'agent' | 'functions' | 'rotate_all' | 'unknown';
export const CREATED_VIA: KeyCreatedVia[] = ['dashboard', 'cli', 'agent', 'functions', 'rotate_all', 'unknown'];

/** 一覧の 1 行（サーバーの SecretKeyView と同じ形）。鍵の値は無い（displayPrefix = 接頭辞 + 4 文字…） */
export interface SecretKeyView {
  id: string;
  projectId: string;
  projectName: string;
  environment: EnvName;
  label: string | null;
  displayPrefix: string;
  createdAt: string;
  lastUsedAt: string | null;
  createdVia: KeyCreatedVia;
  /** Functions の自動の鍵（失効すると次の Functions のデプロイまで Functions から Kumo の API を呼べない） */
  functions: boolean;
}

/** 外したログイン方法（サーバーの RemovedSignInMethod・lib/mail/notices.ts と同じ形） */
export type RemovedSignInMethod =
  | { kind: 'passkey'; label: string; createdAt: string; lastUsedAt: string | null }
  | { kind: 'provider'; provider: string; email: string | null; createdAt: string; lastUsedAt: string | null };

export interface ResetResult {
  removedSignInMethods: RemovedSignInMethod[];
  secretKeys: SecretKeyView[];
}

export interface RotatePreview {
  keys: SecretKeyView[];
  version: string;
}

export interface CreatedKey {
  id: string;
  projectId: string;
  projectName: string;
  environment: EnvName;
  label: string | null;
  /** 新しい鍵の値。この応答の 1 回だけ（画面の state にだけ置く） */
  token: string;
  /** 置き換えた古い鍵の id */
  replaces: string;
}

export interface RotateResult {
  revoked: SecretKeyView[];
  created: CreatedKey[];
  functionsRedeployRequired: Array<{ projectId: string; projectName: string; environment: EnvName }>;
}

// ---------------------------------------------------------------- 応答の読み取り（形の食い違いに強く）

const str = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const env = (v: unknown): EnvName | null => (v === 'development' || v === 'production' ? v : null);

/** 1 行を読む。必須の値（id・プロジェクト・環境・作成日時）が無い行は捨てる（壊れた行で画面全体を落とさない） */
export function readSecretKey(v: unknown): SecretKeyView | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const id = str(o.id);
  const projectId = str(o.projectId);
  const environment = env(o.environment);
  const createdAt = str(o.createdAt);
  if (!id || !projectId || !environment || !createdAt) return null;
  const via = str(o.createdVia);
  return {
    id,
    projectId,
    projectName: str(o.projectName) ?? '',
    environment,
    label: str(o.label),
    displayPrefix: str(o.displayPrefix) ?? '',
    createdAt,
    lastUsedAt: str(o.lastUsedAt),
    // 知らない値（将来の新しい入口）は unknown として出す（文言のキーが無い値を画面に出さない）
    createdVia: via && (CREATED_VIA as string[]).includes(via) ? (via as KeyCreatedVia) : 'unknown',
    functions: o.functions === true
  };
}

export function readRemovedMethod(v: unknown): RemovedSignInMethod | null {
  if (!v || typeof v !== 'object') return null;
  const o = v as Record<string, unknown>;
  const createdAt = str(o.createdAt);
  if (!createdAt) return null;
  const lastUsedAt = str(o.lastUsedAt);
  if (o.kind === 'passkey') return { kind: 'passkey', label: str(o.label) ?? '', createdAt, lastUsedAt };
  if (o.kind === 'provider') {
    const provider = str(o.provider);
    if (!provider) return null;
    return { kind: 'provider', provider, email: str(o.email), createdAt, lastUsedAt };
  }
  return null;
}

const list = <T>(v: unknown, read: (x: unknown) => T | null): T[] => (Array.isArray(v) ? v.map(read).filter((x): x is T => x !== null) : []);

/** 再設定の応答 → 結果。古いサーバー（{ ok: true } だけ）は空の一覧 = 完了の画面を出さずにログインへ */
export function readResetResult(res: unknown): ResetResult {
  const o = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
  return { removedSignInMethods: list(o.removedSignInMethods, readRemovedMethod), secretKeys: list(o.secretKeys, readSecretKey) };
}

/** 完了の画面（/reset-password/done）を挟むか。どちらも空なら今までどおりログインへ直行（見せる物が無い） */
export const hasResetFindings = (r: ResetResult) => r.removedSignInMethods.length > 0 || r.secretKeys.length > 0;

export function readPreview(res: unknown): RotatePreview {
  const o = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
  return { keys: list(o.keys, readSecretKey), version: str(o.version) ?? '' };
}

export function readRotateResult(res: unknown): RotateResult {
  const o = (res && typeof res === 'object' ? res : {}) as Record<string, unknown>;
  const created = list(o.created, (v): CreatedKey | null => {
    if (!v || typeof v !== 'object') return null;
    const c = v as Record<string, unknown>;
    const id = str(c.id);
    const projectId = str(c.projectId);
    const environment = env(c.environment);
    const token = str(c.token);
    if (!id || !projectId || !environment || !token) return null;
    return { id, projectId, projectName: str(c.projectName) ?? '', environment, label: str(c.label), token, replaces: str(c.replaces) ?? '' };
  });
  const functionsRedeployRequired = list(o.functionsRedeployRequired, (v) => {
    if (!v || typeof v !== 'object') return null;
    const f = v as Record<string, unknown>;
    const projectId = str(f.projectId);
    const environment = env(f.environment);
    if (!projectId || !environment) return null;
    return { projectId, projectName: str(f.projectName) ?? '', environment };
  });
  return { revoked: list(o.revoked, readSecretKey), created, functionsRedeployRequired };
}

// ---------------------------------------------------------------- 並べ方

export interface ProjectGroup<T> {
  projectId: string;
  projectName: string;
  items: T[];
}

/**
 * プロジェクトごとにまとめる（サーバーの並び = プロジェクトの作成順 → 環境 → 鍵の作成順 を崩さない = 最初に出た順）。
 * 環境の違いは各行に出す（同じプロジェクトの development と production を 1 つの見出しの下に並べる）
 */
export function groupByProject<T extends { projectId: string; projectName: string }>(items: T[]): ProjectGroup<T>[] {
  const out: ProjectGroup<T>[] = [];
  const byId = new Map<string, ProjectGroup<T>>();
  for (const it of items) {
    let g = byId.get(it.projectId);
    if (!g) {
      g = { projectId: it.projectId, projectName: it.projectName, items: [] };
      byId.set(it.projectId, g);
      out.push(g);
    }
    g.items.push(it);
  }
  return out;
}

/** 鍵の画面（プロジェクトの API キー）へのパス。projectId は URL に入れる前に符号化（サーバーの値でも、パスを壊す文字を通さない） */
export const keysPagePath = (projectId: string, environment: EnvName) => `/p/${encodeURIComponent(projectId)}/${environment}/keys`;

/**
 * Functions の自動の鍵を失効した後の案内のコマンド（環境ごとに 1 行・重複を除く）。
 * CLI の functions は既定 development（FunctionsPage.tsx と同じ）だが、取り違えないよう両方とも --env を明示する。
 * プロジェクトは CLI のフォルダ（kumo.config.json）で決まる = コマンドにプロジェクトは入らない。画面はプロジェクト名を横に出す
 */
export function functionsDeployCommand(cli: string, environment: EnvName): string {
  return `${cli} functions deploy --env ${environment}`;
}

// ---------------------------------------------------------------- 失敗の分け方

type CodeError = { code?: string; details?: Record<string, unknown> } | null | undefined;

export type RotateFailure = 'keys_changed' | 'no_secret_keys' | 'email_unverified' | 'other';

/**
 * rotate-all の失敗 → 画面の出し分け。
 *   keys_changed: preview の後に鍵が増減した → 一覧を取り直して見せ直す（勝手に実行し直さない = 見せた一覧と失効する一覧をずらさない）
 *   no_secret_keys: 0 本（別の端末で消した等）→ 一覧を取り直す（空の表示になる）
 *   email_unverified: 確認メールの案内（EmailUnverifiedNotice）
 * 再認証（400 reauthentication_required / 401 reauthentication_failed）は useReauth の run が先に扱う（ここには来ない）
 */
export function rotateFailure(err: CodeError): RotateFailure {
  const c = err?.code;
  return c === 'keys_changed' || c === 'no_secret_keys' || c === 'email_unverified' ? c : 'other';
}

// ---------------------------------------------------------------- 再設定の結果の受け渡し（メモリだけ）

let pendingReset: ResetResult | null = null;

/** 再設定の成功 → 完了の画面へ移る直前に置く（メモリだけ。URL・ストレージに置かない = 冒頭の WHY） */
export function setResetResult(r: ResetResult | null) {
  pendingReset = r;
}

/**
 * 完了の画面が読む。消すのは clearResetResult（画面の useEffect の中）。
 * WHY 読むと同時に消さないか: 開発中の StrictMode は useState の初期化を 2 回呼ぶ = 2 回目が空になり、完了の画面が出ずにログインへ飛ぶ
 */
export const peekResetResult = (): ResetResult | null => pendingReset;

/** 読み終えたら消す（戻る・進むで古い一覧を出し直さない・メモリにも長く残さない） */
export function clearResetResult() {
  pendingReset = null;
}
